import Offcanvas from 'react-bootstrap/Offcanvas';
import { RiRefreshLine } from '@remixicon/react';
import { useThemeStore, resolveColorMode, type ThemeState } from '@/stores/themeStore';
import { THEME_PRESETS } from '@/lib/theme';

interface ThemeSwitcherPanelProps {
  show: boolean;
  onHide: () => void;
}

/**
 * TEMA AYAR PANELİ.
 *
 * Referans arayüzdeki "switcher" panelinin karşılığı. Buradaki her ayar
 * <html> üzerindeki bir attribute'a ya da bir CSS değişkenine yazılır;
 * hiçbiri React ağacını yeniden kurmaz.
 */
export function ThemeSwitcherPanel({ show, onHide }: ThemeSwitcherPanelProps) {
  const theme = useThemeStore();
  const isDark = resolveColorMode(theme.colorMode) === 'dark';

  return (
    <Offcanvas show={show} onHide={onHide} placement="end" scroll>
      <Offcanvas.Header closeButton className="border-bottom">
        <div>
          <Offcanvas.Title className="fs-15">Tema Ayarları</Offcanvas.Title>
          <div className="fs-11 text-muted">Görünümü kendinize göre düzenleyin</div>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body>
        <Section title="Tema">
          <SegmentedControl<ThemeState['colorMode']>
            value={theme.colorMode}
            onChange={(v) => {
              // Renk modu değişince AA-güvenli varyantın yeniden türetilmesi
              // gerekir; bu yüzden doğrudan setPartial değil setPrimary zinciri.
              theme.setPartial({ colorMode: v });
              theme.setPrimary(theme.primaryHex);
            }}
            options={[
              { value: 'light', label: 'Açık' },
              { value: 'dark', label: 'Koyu' },
              { value: 'system', label: 'Sistem' },
            ]}
          />
        </Section>

        <Section title="Yön">
          <SegmentedControl<ThemeState['direction']>
            value={theme.direction}
            onChange={(v) => theme.setPartial({ direction: v })}
            options={[
              { value: 'ltr', label: 'Soldan Sağa' },
              { value: 'rtl', label: 'Sağdan Sola' },
            ]}
          />
        </Section>

        <Section title="Menü Konumu">
          <SegmentedControl<ThemeState['sidebarState']>
            value={theme.sidebarState}
            onChange={(v) => theme.setPartial({ sidebarState: v })}
            options={[
              { value: 'expanded', label: 'Geniş' },
              { value: 'icon', label: 'İkon' },
              { value: 'hidden', label: 'Gizli' },
            ]}
          />
        </Section>

        <Section title="Menü Rengi">
          <SegmentedControl<ThemeState['menuTone']>
            value={theme.menuTone}
            onChange={(v) => theme.setPartial({ menuTone: v })}
            options={[
              { value: 'light', label: 'Açık' },
              { value: 'dark', label: 'Koyu' },
            ]}
          />
        </Section>

        <Section title="Üst Bar Rengi">
          <SegmentedControl<ThemeState['headerTone']>
            value={theme.headerTone}
            onChange={(v) => theme.setPartial({ headerTone: v })}
            options={[
              { value: 'light', label: 'Açık' },
              { value: 'dark', label: 'Koyu' },
            ]}
          />
        </Section>

        <Section title="Sayfa Genişliği">
          <SegmentedControl<ThemeState['containerWidth']>
            value={theme.containerWidth}
            onChange={(v) => theme.setPartial({ containerWidth: v })}
            options={[
              { value: 'fluid', label: 'Tam' },
              { value: 'boxed', label: 'Sınırlı' },
            ]}
          />
        </Section>

        <Section title="Satır Yoğunluğu">
          <SegmentedControl<ThemeState['density']>
            value={theme.density}
            onChange={(v) => theme.setPartial({ density: v })}
            options={[
              { value: 'comfortable', label: 'Rahat' },
              { value: 'compact', label: 'Sıkışık' },
            ]}
          />
        </Section>

        <Section
          title="Tema Ana Rengi"
          hint="Seçtiğiniz renk, okunabilirlik için otomatik olarak koyu bir metin varyantıyla eşlenir."
        >
          <div className="d-flex flex-wrap gap-2 mb-3">
            {THEME_PRESETS.map((preset) => (
              <button
                key={preset.key}
                type="button"
                className={`theme-swatch${
                  theme.primaryHex.toLowerCase() === preset.hex.toLowerCase() ? ' is-active' : ''
                }`}
                style={{ backgroundColor: preset.hex }}
                onClick={() => theme.setPrimary(preset.hex)}
                aria-label={preset.label}
                title={preset.label}
              />
            ))}
          </div>

          <label className="d-flex align-items-center gap-2 fs-12">
            <span className="text-muted">Özel renk</span>
            <input
              type="color"
              className="form-control form-control-color"
              value={theme.primaryHex}
              onChange={(e) => theme.setPrimary(e.target.value)}
              aria-label="Özel tema rengi seç"
            />
            <code className="fs-11 text-muted">{theme.primaryHex.toUpperCase()}</code>
          </label>

          <div className="mt-3 p-3 rounded" style={{ backgroundColor: 'var(--mo-surface-sunken)' }}>
            <div className="fs-11 text-muted mb-2">Önizleme</div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <button type="button" className="btn btn-sm btn-primary">
                Kaydet
              </button>
              <button type="button" className="btn btn-sm btn-primary-light">
                Onayla
              </button>
              <span className="badge badge-primary-transparent">Etiket</span>
              <a href="#preview" onClick={(e) => e.preventDefault()}>
                Bağlantı
              </a>
            </div>
            <div className="fs-11 text-muted mt-2">
              Metin varyantı: <code>{isDark ? 'açık' : 'koyu'}</code> — AA kontrast garantili
            </div>
          </div>
        </Section>

        <button type="button" className="btn btn-outline-secondary w-100 mt-2" onClick={theme.reset}>
          <RiRefreshLine size={16} className="me-2" aria-hidden="true" />
          Varsayılana Sıfırla
        </button>
      </Offcanvas.Body>
    </Offcanvas>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-4">
      <div className="fs-12 fw-semibold text-strong mb-1">{title}</div>
      {hint && <div className="fs-11 text-muted mb-2">{hint}</div>}
      {children}
    </div>
  );
}

interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: ReadonlyArray<{ value: T; label: string }>;
}

function SegmentedControl<T extends string>({ value, onChange, options }: SegmentedControlProps<T>) {
  return (
    <div className="chart-period-pills w-100" role="group">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`chart-period-pills__btn flex-fill${opt.value === value ? ' is-active' : ''}`}
          onClick={() => onChange(opt.value)}
          aria-pressed={opt.value === value}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
